// File: org-tree-manager/src/Homepage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import teamCollab from './images/team-collab.jpg';
import conference from './images/conference.jpg';
import hrAdvisory from './images/hr-advisory.jpg';
import talentDev from './images/talent-dev.jpg';
import isoAudit from './images/iso-audit.jpg';
import projectSupport from './images/project-support.jpg';
import valueAdded from './images/value-added.jpg';
import faqImage from './images/faq.jpg';
import ladyImage from './images/lady.png';

export default function Homepage() {
  const navigate = useNavigate();
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const [scrollDirectionDown, setScrollDirectionDown] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollArrow(scrollY > 200);
      setScrollDirectionDown(scrollY < 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToggle = () => {
    scrollDirectionDown
      ? window.scrollBy({ top: 500, behavior: 'smooth' })
      : window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardToggle = (index, e) => {
    e.stopPropagation();
    setExpandedCard(expandedCard === index ? null : index);
  };

  const services = [
    {
      title: 'Strategic HR Advisory',
      desc: 'Expert guidance on HR strategy, compliance, and transformation.',
      full: 'We help organizations align HR with business goals, improve compliance, and drive transformation through strategic planning.',
      img: hrAdvisory
    },
    {
      title: 'Talent Acquisition & Development',
      desc: 'End-to-end hiring and upskilling support.',
      full: 'From sourcing to onboarding, we streamline hiring and offer development programs that grow your workforce.',
      img: talentDev
    },
    {
      title: 'ISO Audit Documentation Support',
      desc: 'Structured templates and tracking for ISO readiness.',
      full: 'We provide documentation frameworks and audit prep tools to help you meet ISO standards with confidence.',
      img: isoAudit
    },
    {
      title: 'Project Support / Technical Workforce',
      desc: 'Skilled professionals for short-term or specialized projects.',
      full: 'Deploy technical experts on-demand for critical projects, ensuring speed and precision.',
      img: projectSupport
    },
    {
      title: 'Specialized & Value-Added Services',
      desc: 'Custom HR solutions, analytics, and operational enhancements.',
      full: 'We tailor HR tools and analytics to your unique needs, adding measurable value to your operations.',
      img: valueAdded
    },
    {
      title: 'FAQ',
      desc: 'Answers to common questions about our platform.',
      full: 'Explore frequently asked questions about Talentraxion’s features, pricing, onboarding, and support.',
      img: faqImage
    }
  ];

  return (
    <div className="bg-[#f0f6fc] text-[#1e293b] min-h-screen font-sans relative">
      {/* Scroll Arrow */}
      {showScrollArrow && (
        <button
          onClick={handleScrollToggle}
          className="fixed right-4 bottom-6 bg-[#2563eb] text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition duration-300 z-50"
        >
          {scrollDirectionDown ? '↓' : '↑'}
        </button>
      )}

      {/* Hero Section */}
      <section className="relative text-center py-12 px-6 bg-gradient-to-br from-indigo-50 via-white to-blue-50 overflow-hidden hero-glitter">
        {/* Decorative Orb */}
        <div className="absolute top-[-100px] right-[-100px] w-[400px] h-[400px] bg-gradient-to-tr from-indigo-300 to-blue-400 rounded-full blur-3xl opacity-30 pointer-events-none z-0"></div>

        {/* Headline */}
        <div className="relative z-10 max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold tracking-wide text-gray-800 leading-tight drop-shadow-sm">
            Empowering HR with <span className="text-indigo-600">Scalable</span>,{' '}
            <span className="text-blue-600">Modular</span> Solutions
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            Explore our products and start your journey today.
          </p>

          {/* CTA Buttons */}
          <div className="mt-8 space-x-4">
            <button
              onClick={() => navigate('/products')}
              className="bg-indigo-600 text-white px-5 py-3 rounded-lg shadow hover:bg-indigo-700 transition"
            >
              Explore Products
            </button>
            <button
              onClick={() => navigate('/register')}
              className="bg-white border border-gray-300 text-gray-800 px-5 py-3 rounded-lg shadow hover:bg-gray-100 transition"
            >
              Request Demo
            </button>
          </div>
        </div>

        {/* Cards + Lady Image */}
        <div className="relative z-2 mt-1 flex flex-col lg:flex-row items-center justify-between">
          {/* Floating Cards */}
          <div className="relative w-full lg:w-2/3 h-[360px]">
            {/* ATS */}
            <div className="absolute top-4 left-[5%] animate-spiral-in opacity-0">
              <div className="bg-gradient-to-br from-cyan-400 to-blue-600 text-white p-4 rounded-xl shadow-xl w-[160px] animate-float-x">
                <h3 className="text-lg font-bold mb-1">ATS</h3>
                <p className="text-sm">Streamline hiring</p>
              </div>
            </div>

            {/* Scout */}
            <div className="absolute top-20 left-[30%] animate-bounce-in opacity-0">
              <div className="bg-gradient-to-br from-pink-500 to-purple-600 text-white p-4 rounded-xl shadow-xl w-[160px] animate-float-y">
                <h3 className="text-lg font-bold mb-1">Scout</h3>
                <p className="text-sm">Discover talent</p>
              </div>
            </div>

            {/* HRIS */}
            <div className="absolute bottom-4 left-[15%] animate-swivel-in opacity-0">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-blue-900 p-4 rounded-xl shadow-xl w-[160px] animate-float-rotate">
                <h3 className="text-lg font-bold mb-1">HRIS</h3>
                <p className="text-sm">Empower your workforce</p>
              </div>
            </div>

            {/* AuditReady Suite */}
            <div className="absolute top-32 left-[55%] animate-spiral-in opacity-0">
              <div className="bg-gradient-to-br from-teal-400 to-green-500 text-white p-4 rounded-xl shadow-xl w-[160px] animate-float-x">
                <h3 className="text-lg font-bold mb-1">AuditReady Suite</h3>
                <p className="text-sm">Smart compliance, simplified</p>
              </div>
            </div>

            {/* PerformEdge */}
            <div className="absolute bottom-8 left-[40%] animate-bounce-in opacity-0">
              <div className="bg-gradient-to-br from-indigo-500 to-blue-700 text-white p-4 rounded-xl shadow-xl w-[160px] animate-float-y">
                <h3 className="text-lg font-bold mb-1">PerformEdge</h3>
                <p className="text-sm">Track workforce performances</p>
              </div>
            </div>
          </div>

          {/* Lady Image */}
          <div className="w-full lg:w-1/2 mt-12 lg:mt-0">
            <img src={ladyImage} alt="Smiling woman" className="w-[700px] mx-auto" />
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="flex flex-col md:flex-row items-center justify-between px-6 py-12 bg-[#fdf6ec]">
        <img
          src={teamCollab}
          alt="Team collaboration"
          className="w-full md:w-1/2 rounded-lg shadow-md mb-6 md:mb-0"
        />
        <div className="md:w-1/2 md:pl-12">
          <h2 className="text-2xl font-bold mb-4">Who We Are</h2>
          <p className="text-[#475569] text-lg">
            Talentraxion is a modular HR platform built to empower organizations with clarity,
            scalability, and empathy. We believe in systems that serve people—not the other way
            around.
          </p>
        </div>
      </section>

      {/* Why Choose Talentraxion */}
      <section className="flex flex-col md:flex-row items-center justify-between px-6 py-12 bg-[#e0f7f4]">
        <div className="md:w-1/2 md:pr-12">
          <h2 className="text-2xl font-bold mb-4">Why Choose Talentraxion</h2>
          <ul className="list-disc list-inside text-[#475569] text-lg space-y-2">
            <li>
              <strong>Customizable Solutions:</strong> Tailored to your workflows and goals
            </li>
            <li>
              <strong>Result-Oriented Approach:</strong> Built for measurable impact
            </li>
          </ul>
        </div>
        <img
          src={conference}
          alt="Professional setting"
          className="w-full md:w-1/2 rounded-lg shadow-md mt-6 md:mt-0"
        />
      </section>

      {/* Our Services */}
      <section className="px-6 py-12 bg-[#f8f9fa]">
        <h2 className="text-2xl font-bold text-center mb-8">Our Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              onClick={(e) => handleCardToggle(index, e)}
              className={`bg-white p-1 rounded shadow cursor-pointer transition-all duration-300 ${
                expandedCard === index ? 'max-h-[500px]' : 'max-h-[250px]'
              } overflow-hidden hover:shadow-lg`}
            >
              <img
                src={service.img}
                alt={service.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
              <p className="text-[#475569]">
                {expandedCard === index ? service.full : service.desc}
              </p>
              <p className="text-blue-600 mt-2">
                {expandedCard === index ? 'Click anywhere else to collapse' : 'Read more...'}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-6 py-12 bg-[#fff0f5]">
        <h2 className="text-2xl font-bold text-center mb-8">What Our Clients Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              name: 'Ayesha Rahman',
              role: 'HR Director, Finovate',
              quote:
                'Talentraxion transformed our hiring process. The modular tools are intuitive and scalable.'
            },
            {
              name: 'Md. Imran Hossain',
              role: 'COO, TechNest',
              quote: 'We finally have a system that adapts to our workflows. Support is phenomenal.'
            },
            {
              name: 'Sarah Chowdhury',
              role: 'Founder, PeoplePulse',
              quote: 'From onboarding to compliance, Talentraxion delivers clarity and control.'
            }
          ].map((testimonial, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
              <p className="text-gray-700 italic mb-4">“{testimonial.quote}”</p>
              <div className="text-sm text-gray-500">
                <strong>{testimonial.name}</strong>
                <br />
                {testimonial.role}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a8a] text-white text-center py-6 mt-12">
        <p className="text-sm mb-2">RELIABLE I.T. SERVICES PRIVATE LIMITED</p>
        <p className="text-sm mb-2">Sector V, Salt Lake, Kolkata, India</p>
        <p className="text-sm mb-2">info@talentraxion.com</p>
        <p className="text-sm mb-2">
          Website:{' '}
          <a href="https://www.talentraxion.com" className="underline hover:text-blue-200">
            www.talentraxion.com
          </a>
        </p>
        <div className="flex justify-center space-x-4 mt-2">
          <a href="#" className="hover:underline">
            Facebook
          </a>
          <a href="#" className="hover:underline">
            Twitter
          </a>
          <a href="#" className="hover:underline">
            LinkedIn
          </a>
        </div>
      </footer>
    </div>
  );
}
